import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, toRon } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Landmark, CreditCard, PiggyBank, ScrollText, Loader2, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { vadesiz: "Vadesiz Hesap", vadeli: "Vadeli Hesap", kredi: "Kredi Kartı" };
const TYPE_ICON = { vadesiz: Landmark, vadeli: PiggyBank, kredi: CreditCard };
const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#F43F5E", "#8B5CF6", "#06B6D4"];

export default function BankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", bank_name: "", iban: "", account_type: "vadesiz", currency: "RON", balance: "", color: "#10B981" });
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmt, setStmt] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);

  const openStatement = async (a) => {
    setStmtOpen(true);
    setStmt(null);
    setStmtLoading(true);
    try {
      setStmt(await api.accountStatement(a.id, 80));
    } catch {
      toast.error("Döküm yüklenemedi");
      setStmtOpen(false);
    } finally {
      setStmtLoading(false);
    }
  };

  const load = async () => setAccounts(await api.accounts());
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!form.name || !form.bank_name) {
      toast.error("Hesap adı ve banka zorunludur");
      return;
    }
    await api.createAccount({ ...form, balance: parseFloat(form.balance) || 0 });
    toast.success("Hesap eklendi");
    setOpen(false);
    setForm({ name: "", bank_name: "", iban: "", account_type: "vadesiz", currency: "RON", balance: "", color: "#10B981" });
    load();
  };

  const remove = async (id) => {
    try {
      await api.deleteAccount(id);
      toast.success("Hesap silindi");
      load();
    } catch (e) {
      toast.error("Hesap silinemedi");
    }
  };

  const total = accounts.reduce((s, a) => s + toRon(a.balance, a.currency), 0);

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="Banka Hesapları"
        subtitle={`Toplam bakiye (RON): ${money(total)}`}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-account-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="h-4 w-4" /> <span className="ml-1.5">Yeni Hesap</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader>
                <DialogTitle className="font-display">Yeni Banka Hesabı</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Hesap Adı</Label><Input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Ana İşletme Hesabı" /></div>
                <div><Label>Banka</Label><Input data-testid="account-bank-input" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1.5" placeholder="Banca Transilvania" /></div>
                <div><Label>IBAN</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="mt-1.5" placeholder="RO49..." /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tür</Label>
                    <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vadesiz">Vadesiz Hesap</SelectItem>
                        <SelectItem value="vadeli">Vadeli Hesap</SelectItem>
                        <SelectItem value="kredi">Kredi Kartı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Para Birimi</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger data-testid="account-currency-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RON">RON (lei)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Bakiye</Label><Input data-testid="account-balance-input" type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className="mt-1.5" placeholder="0,00" /></div>
                <div>
                  <Label>Renk</Label>
                  <div className="mt-2 flex gap-2">
                    {COLORS.map((c) => (
                      <button key={c} onClick={() => setForm({ ...form, color: c })} className={`h-7 w-7 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-offset-card ring-white scale-110" : ""}`} style={{ background: c }} />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="account-submit-button" onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {accounts.map((a) => {
          const Icon = TYPE_ICON[a.account_type] || Landmark;
          const neg = a.balance < 0;
          return (
            <div key={a.id} data-testid="account-card" onClick={() => openStatement(a)} className="group relative cursor-pointer overflow-hidden rounded-xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40">
              <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 rounded-full opacity-10 blur-2xl" style={{ background: a.color }} />
              <div className="relative flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${a.color}22`, color: a.color }}>
                  <Icon className="h-5 w-5" />
                </div>
                <button data-testid="delete-account-button" onClick={(e) => { e.stopPropagation(); remove(a.id); }} className="relative z-10 text-muted-foreground opacity-0 transition-opacity hover:text-rose-400 group-hover:opacity-100">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">{TYPE_LABEL[a.account_type]}</span>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-foreground">{a.currency}</span>
                </div>
                <div className="mt-0.5 font-medium">{a.name}</div>
                <div className="text-xs text-muted-foreground">{a.bank_name}</div>
              </div>
              <div className={`mt-4 font-mono text-2xl font-semibold tabular ${neg ? "text-rose-400" : "text-foreground"}`}>{money(a.balance, a.currency)}</div>
              {a.iban && <div className="mt-2 truncate font-mono text-xs text-muted-foreground">{a.iban}</div>}
              <div className="mt-3 flex items-center gap-1 text-xs text-primary opacity-0 transition-opacity group-hover:opacity-100">
                <ScrollText className="h-3.5 w-3.5" /> Güncel dökümü gör
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-8">
              <ScrollText className="h-5 w-5 text-primary" />
              {stmt ? `${stmt.account.name} — Güncel Döküm` : "Hesap Dökümü"}
            </DialogTitle>
          </DialogHeader>
          {stmtLoading && <div className="py-12 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>}
          {stmt && !stmtLoading && (
            <>
              <div className="flex items-center justify-between rounded-lg bg-secondary/40 px-4 py-2.5 text-sm">
                <span className="text-muted-foreground">{stmt.account.bank_name} · {stmt.account.currency}</span>
                <span>Güncel bakiye: <span className={`font-mono font-semibold ${stmt.account.balance < 0 ? "text-rose-400" : "text-emerald-400"}`}>{money(stmt.account.balance, stmt.account.currency)}</span></span>
              </div>
              <div className="mt-3 max-h-[55vh] overflow-y-auto rounded-lg border border-border" data-testid="statement-list">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card">
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-4 py-2.5 font-semibold">Tarih</th>
                      <th className="px-4 py-2.5 font-semibold">Açıklama</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Tutar</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Bakiye</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stmt.movements.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Bu hesaba ait hareket bulunamadı</td></tr>}
                    {stmt.movements.map((m, i) => (
                      <tr key={i} data-testid="statement-row" className="border-b border-border/50 hover:bg-secondary/30">
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-muted-foreground">{m.date}</td>
                        <td className="px-4 py-2">{m.description}</td>
                        <td className={`whitespace-nowrap px-4 py-2 text-right font-mono ${m.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {m.type === "income" ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
                            {m.type === "income" ? "+" : "-"}{money(m.amount, m.currency)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-muted-foreground">{m.balance_after != null ? money(m.balance_after, m.currency) : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Toplam {stmt.total} hareket · en güncel {stmt.count} tanesi gösteriliyor</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
